import { Component, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import {
  LoadingController,
  NavController,
  NavParams,
  ToastController,
} from "ionic-angular";
import { ClearinghouseDetail } from "models/payers/payer";
import { PayersService } from "providers/payers-service/payers.service";

// "Configure Clearinghouse Profile" — the create form for an SFTP clearinghouse profile
// (CO-674, under CO-668 / epic CO-428). Pushed by CLASS from the ClearingHouseView + FAB;
// on a successful save it reloads the parent grid and pops back.
//
// Backend contract: POST {apiUrl}/claim-submission/clearinghouse
// (claim-submission-save-clearinghouse-profiles). We send ONLY the profile fields — the
// backend treats a missing/0 clearinghouseId as a CREATE (mints the next id from the
// counter) and stamps tenantId + audit fields from the authenticated user. It does NOT
// validate the fields, so the AC's validations are enforced HERE so we never persist junk
// that would later break live 837 claim submission.

// A hostname (RFC-1123 label form) OR an IPv4 address — sftpHostAddress must be reachable.
const HOST_PATTERN =
  /^(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|(?:\d{1,3}\.){3}\d{1,3})$/;
// clearinghouseProfileName: alphanumeric, hyphens and underscores only.
const PROFILE_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

// Angular's Validators.required accepts any string of length > 0, so "   " passes it and
// is persisted verbatim. Every field here is either an SFTP credential or a positional
// X12 element — a whitespace-only value is never a real answer.
export function requiredNonBlank(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || String(value).trim() === "") {
    return { required: true };
  }
  return null;
}

@Component({
  selector: "configure-clearinghouse",
  templateUrl: "configure-clearinghouse.html",
})
export class ConfigureClearinghousePage implements OnInit {
  form: FormGroup;
  saving: boolean = false;
  /** Profile names already in use by this tenant, supplied by the grid that opened the
   *  form. The name is the only handle a profile has downstream (Payer/EDI shows nothing
   *  else) and there is no delete, so a duplicate is permanent and ambiguous. */
  existingProfileNames: string[] = [];
  cardHeader: string = "Configure Clearinghouse Profile";

  // The fields shown on the form, in render order, with the label + input type each needs.
  // EDI-envelope fields (ISA*/GS*/NM109) feed live 837 formatting, so they are required and
  // part of the create contract. `group` drives the two on-screen sections.
  // maxLength on the EDI-envelope fields is the X12 element width (ISA is fixed-width;
  // GS02/GS03 and NM109 are bounded). The Go handler does not check them, and an
  // over-long element does not fail loudly — it produces an 837 the clearinghouse
  // rejects, days later, for the whole batch.
  static readonly FIELD_META: {
    key: string;
    label: string;
    type: string;
    group: string;
    maxLength: number;
  }[] = [
    { key: "clearinghouseName", label: "Clearinghouse Name", type: "text", group: "Connection", maxLength: 128 },
    { key: "clearinghouseProfileName", label: "Clearinghouse Profile Name", type: "text", group: "Connection", maxLength: 64 },
    { key: "sftpUserName", label: "SFTP Username", type: "text", group: "Connection", maxLength: 64 },
    { key: "sftpHostAddress", label: "SFTP Host Address", type: "text", group: "Connection", maxLength: 255 },
    { key: "sftpPassword", label: "SFTP Password", type: "password", group: "Connection", maxLength: 128 },
    { key: "ISA05", label: "ISA05", type: "text", group: "EDI envelope", maxLength: 2 },
    { key: "ISA06", label: "ISA06", type: "text", group: "EDI envelope", maxLength: 15 },
    { key: "ISA07", label: "ISA07", type: "text", group: "EDI envelope", maxLength: 2 },
    { key: "ISA08", label: "ISA08", type: "text", group: "EDI envelope", maxLength: 15 },
    { key: "ISA15", label: "ISA15", type: "text", group: "EDI envelope", maxLength: 1 },
    { key: "GS02", label: "GS02", type: "text", group: "EDI envelope", maxLength: 15 },
    { key: "GS03", label: "GS03", type: "text", group: "EDI envelope", maxLength: 15 },
    { key: "NM109_100A", label: "NM109 (Loop 1000A)", type: "text", group: "EDI envelope", maxLength: 80 },
  ];

  constructor(
    private fb: FormBuilder,
    private payersService: PayersService,
    private navCtrl: NavController,
    private navParams: NavParams,
    private loader: LoadingController,
    private toastCtrl: ToastController
  ) {}

  ngOnInit(): void {
    this.existingProfileNames = (
      this.navParams.get("existingProfileNames") || []
    ).filter((n) => typeof n === "string");

    const max = (key: string) =>
      Validators.maxLength(ConfigureClearinghousePage.maxLengthFor(key));

    this.form = this.fb.group({
      clearinghouseName: ["", [requiredNonBlank, max("clearinghouseName")]],
      clearinghouseProfileName: [
        "",
        [
          requiredNonBlank,
          Validators.pattern(PROFILE_NAME_PATTERN),
          max("clearinghouseProfileName"),
          this.uniqueProfileName.bind(this),
        ],
      ],
      sftpUserName: ["", [requiredNonBlank, max("sftpUserName")]],
      sftpHostAddress: [
        "",
        [
          requiredNonBlank,
          Validators.pattern(HOST_PATTERN),
          max("sftpHostAddress"),
        ],
      ],
      sftpPassword: [
        "",
        [requiredNonBlank, Validators.minLength(8), max("sftpPassword")],
      ],
      ISA05: ["", [requiredNonBlank, max("ISA05")]],
      ISA06: ["", [requiredNonBlank, max("ISA06")]],
      ISA07: ["", [requiredNonBlank, max("ISA07")]],
      ISA08: ["", [requiredNonBlank, max("ISA08")]],
      ISA15: ["", [requiredNonBlank, max("ISA15")]],
      GS02: ["", [requiredNonBlank, max("GS02")]],
      GS03: ["", [requiredNonBlank, max("GS03")]],
      NM109_100A: ["", [requiredNonBlank, max("NM109_100A")]],
    });
  }

  /** Case-insensitive, whitespace-insensitive: "waystar-medicare " is the same profile
   *  as "Waystar-Medicare" to a human reading the Payer/EDI picker. */
  uniqueProfileName(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (typeof value !== "string" || value.trim() === "") {
      return null;
    }
    const taken = this.existingProfileNames.some(
      (n) => (n || "").trim().toLowerCase() === value.trim().toLowerCase()
    );
    return taken ? { duplicate: true } : null;
  }

  static maxLengthFor(key: string): number {
    const meta = ConfigureClearinghousePage.FIELD_META.filter(
      (f) => f.key === key
    )[0];
    return meta ? meta.maxLength : 128;
  }

  get fieldMeta() {
    return ConfigureClearinghousePage.FIELD_META;
  }

  get connectionFields() {
    return ConfigureClearinghousePage.FIELD_META.filter(
      (f) => f.group === "Connection"
    );
  }

  get ediFields() {
    return ConfigureClearinghousePage.FIELD_META.filter(
      (f) => f.group === "EDI envelope"
    );
  }

  valid(control: string): boolean {
    const c = this.form.get(control);
    return !c || c.valid;
  }

  touched(control: string): boolean {
    const c = this.form.get(control);
    return !!c && c.touched;
  }

  errorFor(control: string): string | null {
    const c = this.form.get(control);
    if (!c || c.valid || !(c.touched || c.dirty)) {
      return null;
    }
    const errors: ValidationErrors = c.errors || {};
    if (errors.required) {
      return "Required";
    }
    if (control === "sftpPassword" && errors.minlength) {
      return "Must be at least 8 characters";
    }
    if (control === "sftpHostAddress" && errors.pattern) {
      return "Enter a valid hostname or IP address";
    }
    if (control === "clearinghouseProfileName" && errors.pattern) {
      return "Letters, numbers, hyphens and underscores only";
    }
    if (control === "clearinghouseProfileName" && errors.duplicate) {
      return "A profile with this name already exists";
    }
    if (errors.maxlength) {
      const n = ConfigureClearinghousePage.maxLengthFor(control);
      return `Must be at most ${n} character${n === 1 ? "" : "s"}`;
    }
    return "Invalid";
  }

  close(): void {
    this.navCtrl.pop();
  }

  save(): void {
    if (this.saving) {
      return;
    }
    // Normalize BEFORE validating: a pasted " sshftp.waystar.com " should be accepted and
    // stored clean, not rejected as an invalid hostname, and a field holding only spaces
    // should collapse to empty so requiredNonBlank reports it as Required.
    this.normalizeWhitespace();

    // Surface every field's validation state before blocking the save.
    Object.keys(this.form.controls).forEach((k) =>
      this.form.get(k).markAsTouched()
    );
    if (this.form.invalid) {
      return;
    }

    this.saving = true;
    const loading = this.loader.create({
      content: "Saving clearinghouse profile...",
    });
    loading.present();

    // Only the profile fields — clearinghouseId is omitted so the backend creates a new
    // profile, and tenantId/audit fields are stamped server-side. The values were
    // normalized above, so nothing here carries stray whitespace into the EDI envelope,
    // which is positional.
    const payload = this.form.value as ClearinghouseDetail;

    this.payersService.saveClearinghouseProfile(payload).subscribe(
      () => {
        loading.dismiss();
        this.saving = false;
        this.presentToast("Clearinghouse profile saved.", "success");
        const parent = this.navParams.get("parent");
        if (parent && typeof parent.loadClearinghouses === "function") {
          parent.loadClearinghouses();
        }
        this.navCtrl.pop();
      },
      () => {
        loading.dismiss();
        this.saving = false;
        this.presentToast(
          "Could not save the clearinghouse profile. Please try again.",
          "error"
        );
      }
    );
  }

  // The SFTP password is the one field left verbatim — leading or trailing spaces can
  // genuinely be part of it.
  private normalizeWhitespace(): void {
    Object.keys(this.form.controls).forEach((key) => {
      if (key === "sftpPassword") {
        return;
      }
      const control = this.form.get(key);
      const value = control.value;
      if (typeof value === "string" && value !== value.trim()) {
        control.setValue(value.trim());
      }
    });
  }

  private presentToast(message: string, cssClass: string): void {
    this.toastCtrl
      .create({
        message,
        duration: 4000,
        position: "below",
        cssClass,
      })
      .present();
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/clearinghouse-view/configure-clearinghouse/configure-clearinghouse.ts